/** Which camera the face check-in modal opens.
 *
 *  The modal used to ask for `facingMode: 'user'` and take whatever came back.
 *  On Windows no webcam reports which way it faces, so Chromium hands over the
 *  SYSTEM DEFAULT device — and a virtual camera driver (DroidCam, OBS, ...)
 *  registers itself as that default. With DroidCam's phone disconnected the
 *  modal then stared at a static placeholder frame and reported "no face" on
 *  every tick. The website never showed it because Chrome remembers a per-site
 *  camera choice; the Electron desktop build has no such memory, so it hit the
 *  default every single time.
 *
 *  Two rules, in order:
 *    1. a camera the user picked here before (localStorage), if still present;
 *    2. the first PHYSICAL camera — one whose label does not look like a
 *       virtual-camera driver. If every camera is virtual, the first one.
 *  Labels are only readable after a permission grant, so on a first-ever run
 *  the caller opens the default, re-enumerates, and switches once if rule 2
 *  now names a different device. */

export const FACE_CAMERA_KEY = 'faceCameraDevice';

/** Virtual-camera drivers seen in the wild. Case-insensitive, on the label. A
 *  false positive costs one manual pick in the dropdown; a miss costs the
 *  orange "no face" panel, so err on the side of listing. */
const VIRTUAL_CAMERA = /droidcam|obs|virtual|manycam|snap camera|xsplit|iriun|epoccam|camo|iviCam|nvidia broadcast|mmhmm/i;

export function isVirtualCamera(label: string | null | undefined): boolean {
  return VIRTUAL_CAMERA.test(label || '');
}

export function readStoredCamera(): string | null {
  try {
    return localStorage.getItem(FACE_CAMERA_KEY) || null;
  } catch {
    return null;
  }
}

export function storeCamera(deviceId: string | null): void {
  try {
    if (deviceId) localStorage.setItem(FACE_CAMERA_KEY, deviceId);
    else localStorage.removeItem(FACE_CAMERA_KEY);
  } catch {
    // Applies for this session only.
  }
}

export function videoInputs(devices: MediaDeviceInfo[]): MediaDeviceInfo[] {
  return devices.filter((d) => d.kind === 'videoinput');
}

/** The camera to open, given what the browser can see right now. `null` means
 *  "nothing to prefer — let the browser choose", which is the first-run case
 *  where labels are blank and every device looks the same. */
export function chooseCamera(devices: MediaDeviceInfo[], stored: string | null): MediaDeviceInfo | null {
  const cams = videoInputs(devices);
  if (!cams.length) return null;
  if (stored) {
    const kept = cams.find((d) => d.deviceId === stored);
    if (kept) return kept;
  }
  // No labels yet (pre-permission): nothing to distinguish them by.
  if (cams.every((d) => !d.label)) return null;
  return cams.find((d) => !isVirtualCamera(d.label)) || cams[0];
}

export function cameraLabel(device: MediaDeviceInfo | null | undefined, index = 0): string {
  return device?.label || `Camera ${index + 1}`;
}
